/*
 * The `stateReconciler` for the ui persist config. Pure and idempotent, rehydrate path only — same contract as `~/store/settings/reconcile`.
 */
import { DOCK_PANEL_ID_SET, MONITOR_IDS } from '~/comps/Dock/ids'

const logger = console.scope('ui-reconcile', true)

const EDGES = new Set(['left', 'right', 'top', 'bottom'])
const DIRECTIONS = new Set(['left', 'right', 'above', 'below'])

// The shape `describePlacement` writes; anything else would throw in `resolveAnchor` during the dock's `onReady`.
const isPlacement = p =>
	!!p &&
	Array.isArray(p.siblings) &&
	p.siblings.every(s => typeof s === 'string') &&
	(p.edge === undefined || EDGES.has(p.edge)) &&
	(p.referencePanel === undefined || DIRECTIONS.has(p.direction))

function reconcileDock(persisted, current) {
	if (!persisted || typeof persisted !== 'object' || persisted.version !== current.version) {
		if (persisted) logger.warn('reconcileUI', `dock version ${persisted.version} ≠ ${current.version} — reset`)
		return current
	}
	let layout = persisted.layout ?? null
	if (layout) {
		const panels = layout.panels
		const ok =
			panels &&
			typeof panels === 'object' &&
			Object.values(panels).every(p => DOCK_PANEL_ID_SET.has(p?.contentComponent)) &&
			MONITOR_IDS.every(id => panels[id])
		if (!ok) {
			logger.warn(
				'reconcileUI',
				'stored dock layout names an unknown panel or lacks a monitor — using the default layout'
			)
			layout = null
		}
	}
	const placements = { ...current.placements }
	for (const [id, placement] of Object.entries(persisted.placements ?? {})) {
		if (DOCK_PANEL_ID_SET.has(id) && isPlacement(placement)) placements[id] = placement
	}
	const collapsed = {}
	for (const [id, height] of Object.entries(persisted.collapsed ?? {})) {
		if (DOCK_PANEL_ID_SET.has(id) && Number.isFinite(height)) collapsed[id] = height
	}
	return { ...current, ...persisted, layout, placements, collapsed }
}

function reconcilePanels(persisted, current) {
	if (!persisted || typeof persisted !== 'object') return current
	// One level deeper, so a section added since the last save gets its collapse default and a save older than `zoom` gets the map.
	const merged = {
		...current,
		...persisted,
		collapsed: { ...current.collapsed, ...persisted.collapsed },
		zoom: { ...current.zoom, ...persisted.zoom },
	}
	if (merged.sizes?.view_split !== undefined) {
		const { view_split, ...sizes } = merged.sizes
		merged.sizes = sizes
	}
	return merged
}

export default function reconcileUI(persisted, current) {
	if (!persisted || typeof persisted !== 'object') return current
	return {
		...current,
		...persisted,
		// A flag added since the user's last visit takes its default.
		show: { ...current.show, ...persisted.show },
		dock: reconcileDock(persisted.dock, current.dock),
		panels: reconcilePanels(persisted.panels, current.panels),
	}
}
